/**
 * Компонент списка токенов регистрации устройств
 * Дата создания: 29 июля 2025, 20:20 MSK
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Block as RevokeIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { registrationTokenApi } from '../../services/registrationTokenApi';
import { invalidateRegistrationData } from '../../utils/cacheInvalidation';
import type {
  RegistrationTokenList,
  RegistrationTokenCreate,
  RegistrationToken
} from '../../services/registrationTokenApi';

const deviceTypeIcons = {
  tv: '📺',
  monitor: '🖥️',
  tablet: '📱',
  led_panel: '💡',
  video_wall: '🖼️',
  desktop_pc: '💻',
  raspberry_pi: '🍓',
  intel_nuc: '📟',
  android_tv: '📺',
};

const deviceTypeLabels = {
  tv: 'Smart TV',
  monitor: 'Монитор',
  tablet: 'Планшет',
  led_panel: 'LED панель',
  video_wall: 'Видеостена',
  desktop_pc: 'Настольный ПК',
  raspberry_pi: 'Raspberry Pi',
  intel_nuc: 'Intel NUC',
  android_tv: 'Android TV',
};

interface Props {
  onTokenCreate?: (token: RegistrationToken) => void;
}

export const RegistrationTokensList: React.FC<Props> = ({ onTokenCreate }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [showUsed, setShowUsed] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<RegistrationToken | null>(null);

  // Состояние формы создания токена
  const [formData, setFormData] = useState<RegistrationTokenCreate>({
    name: '',
    location: '',
    device_type: 'monitor',
    description: '',
    expires_hours: 24,
  });

  // Запрос списка токенов
  const { data: tokens = [], isLoading, error } = useQuery({
    queryKey: ['registrationTokens', { showUsed, showExpired }],
    queryFn: () => registrationTokenApi.getTokens({
      include_used: showUsed,
      include_expired: showExpired,
    }),
  });

  // Мутация создания токена
  const createTokenMutation = useMutation({
    mutationFn: registrationTokenApi.createToken,
    onSuccess: (newToken) => {
      invalidateRegistrationData(queryClient);
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        location: '',
        device_type: 'monitor',
        description: '',
        expires_hours: 24,
      });
      enqueueSnackbar(t('registration.tokenCreatedSuccessfully'), { variant: 'success' });
      onTokenCreate?.(newToken);
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  // Мутация отзыва токена
  const revokeTokenMutation = useMutation({
    mutationFn: registrationTokenApi.revokeToken,
    onSuccess: () => {
      invalidateRegistrationData(queryClient);
      enqueueSnackbar(t('registration.tokenRevokedSuccessfully'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  const handleCreateToken = () => {
    createTokenMutation.mutate(formData);
  };

  const handleRevokeToken = (tokenId: number) => {
    if (window.confirm(t('registration.confirmRevokeToken'))) {
      revokeTokenMutation.mutate(tokenId);
    }
  };

  const handleViewToken = async (tokenId: number) => {
    try {
      const token = await registrationTokenApi.getToken(tokenId);
      setSelectedToken(token);
      setViewDialogOpen(true);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    enqueueSnackbar(t('registration.tokenCopiedToClipboard'), { variant: 'success' });
  };

  const getStatusChip = (token: RegistrationTokenList) => {
    if (token.is_used) {
      return <Chip icon={<CheckIcon />} label={t('registration.status.used')} color="success" size="small" />;
    }

    const isExpired = new Date(token.expires_at) < new Date();
    if (isExpired) {
      return <Chip icon={<CancelIcon />} label={t('registration.status.expired')} color="error" size="small" />;
    }

    return <Chip icon={<ScheduleIcon />} label={t('registration.status.active')} color="primary" size="small" />;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
  };

  if (error) {
    return (
      <Alert severity="error">
        {t('registration.errorLoadingTokens')}: {(error as any).message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Заголовок и действия */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('registration.title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('registration.createToken')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => invalidateRegistrationData(queryClient)}
          >
            {t('common.refresh')}
          </Button>
        </Stack>
      </Box>

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={showUsed}
                  onChange={(e) => setShowUsed(e.target.checked)}
                />
              }
              label={t('registration.showUsedTokens')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showExpired}
                  onChange={(e) => setShowExpired(e.target.checked)}
                />
              }
              label={t('registration.showExpiredTokens')}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Таблица токенов */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('registration.deviceName')}</TableCell>
              <TableCell>{t('registration.deviceType')}</TableCell>
              <TableCell>{t('registration.location')}</TableCell>
              <TableCell>{t('registration.status')}</TableCell>
              <TableCell>{t('registration.createdAt')}</TableCell>
              <TableCell>{t('registration.expiresAt')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('common.loading')}...
                </TableCell>
              </TableRow>
            ) : tokens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('registration.noTokensFound')}
                </TableCell>
              </TableRow>
            ) : (
              tokens.map((token) => (
                <TableRow key={token.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {token.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {token.token}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <span style={{ marginRight: 8 }}>
                        {deviceTypeIcons[token.device_type as keyof typeof deviceTypeIcons] || '🖥️'}
                      </span>
                      {deviceTypeLabels[token.device_type as keyof typeof deviceTypeLabels] || token.device_type}
                    </Box>
                  </TableCell>
                  <TableCell>{token.location || '—'}</TableCell>
                  <TableCell>{getStatusChip(token)}</TableCell>
                  <TableCell>{formatDate(token.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(token.expires_at)}
                    </Typography>
                    {new Date(token.expires_at) < new Date() && (
                      <Typography variant="caption" color="error">
                        {t('registration.expired')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('registration.viewToken')}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewToken(token.id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {!token.is_used && !token.is_expired && (
                        <Tooltip title={t('registration.revokeToken')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeToken(token.id)}
                          >
                            <RevokeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания токена */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('registration.createNewToken')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('registration.deviceName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('registration.location')}
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
            <FormControl fullWidth required>
              <InputLabel>{t('registration.deviceType')}</InputLabel>
              <Select
                value={formData.device_type}
                label={t('registration.deviceType')}
                onChange={(e) => setFormData({ ...formData, device_type: e.target.value as any })}
              >
                {Object.entries(deviceTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Box display="flex" alignItems="center">
                      <span style={{ marginRight: 8 }}>
                        {deviceTypeIcons[value as keyof typeof deviceTypeIcons]}
                      </span>
                      {label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t('registration.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              type="number"
              label={t('registration.expiresHours')}
              value={formData.expires_hours}
              onChange={(e) => setFormData({ ...formData, expires_hours: parseInt(e.target.value) || 24 })}
              inputProps={{ min: 1, max: 168 }}
              helperText={t('registration.expiresHoursHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreateToken}
            variant="contained"
            disabled={!formData.name || createTokenMutation.isPending}
          >
            {createTokenMutation.isPending ? t('common.creating') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог просмотра токена */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('registration.tokenDetails')}</DialogTitle>
        <DialogContent>
          {selectedToken && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('registration.registrationToken')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontFamily="monospace" sx={{
                    backgroundColor: 'grey.100',
                    padding: 1,
                    borderRadius: 1,
                    flex: 1,
                    wordBreak: 'break-all'
                  }}>
                    {selectedToken.token}
                  </Typography>
                  <Tooltip title={t('registration.copyToken')}>
                    <IconButton onClick={() => handleCopyToken(selectedToken.token)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                fullWidth
                label={t('registration.deviceName')}
                value={selectedToken.name}
                InputProps={{ readOnly: true }}
              />

              {selectedToken.location && (
                <TextField
                  fullWidth
                  label={t('registration.location')}
                  value={selectedToken.location}
                  InputProps={{ readOnly: true }}
                />
              )}

              <TextField
                fullWidth
                label={t('registration.deviceType')}
                value={deviceTypeLabels[selectedToken.device_type as keyof typeof deviceTypeLabels] || selectedToken.device_type}
                InputProps={{ readOnly: true }}
              />

              {selectedToken.description && (
                <TextField
                  fullWidth
                  label={t('registration.description')}
                  value={selectedToken.description}
                  multiline
                  rows={3}
                  InputProps={{ readOnly: true }}
                />
              )}

              <TextField
                fullWidth
                label={t('registration.createdAt')}
                value={formatDate(selectedToken.created_at)}
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label={t('registration.expiresAt')}
                value={formatDate(selectedToken.expires_at)}
                InputProps={{ readOnly: true }}
              />

              {selectedToken.used_at && (
                <TextField
                  fullWidth
                  label={t('registration.usedAt')}
                  value={formatDate(selectedToken.used_at)}
                  InputProps={{ readOnly: true }}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationTokensList;
